import { useForm } from "@inertiajs/react";
import PageHeader from "@/components/PageHeader";
import Button from "@/components/buttons/Button";
import InertiaLink from "@/components/buttons/InertiaLink";
import CreditEntries, { CreditGroup } from "@/components/inputs/CreditEntries";
import { CreditTypeOptions } from "@/components/inputs/RoleSearchInput";
import { Artwork } from "@/components/ArtworkSlot";
import FormActions from "@/components/buttons/FormActions";

interface CreditsEditProps {
  track: {
    id: number;
    title: string;
    artwork_attached: boolean;
  };
  artwork: Artwork;
  current_credits: CreditGroup[];
  credit_type_options: CreditTypeOptions;
}

const Edit: React.FC<CreditsEditProps> = ({
  track,
  artwork,
  current_credits,
  credit_type_options,
}) => {
  const { data, setData, put, processing, errors, transform } = useForm({
    credits: current_credits,
  });

  transform((data) => ({
    credits: data.credits.flatMap((group) =>
      group.contributors.map((c) => ({
        contributor_id: c.value,
        role: group.role,
      })),
    ),
  }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    put(`/tracks/${track.id}/credits`);
  };

  return (
    <div className="flex w-full flex-col items-start justify-start gap-5 md:gap-10">
      <PageHeader
        backLink={{
          href: `/tracks/${track.id}/credits`,
          text: "Back to credits",
        }}
        title={`Edit ${track.title} Credits`}
        secondaryAction={{
          href: `/tracks/${track.id}/credits`,
          text: "Cancel",
        }}
        artwork={{
          trackId: track.id,
          uploaded: track.artwork_attached,
          thumbnailUrl: artwork.thumbnail_url,
          originalUrl: artwork.original_url,
          alt: `${track.title} artwork`,
        }}
      />
      <form
        onSubmit={handleSubmit}
        className="flex w-full flex-col gap-5 md:gap-10"
      >
        <div className="dark:bg-cream-950 flex w-full flex-col gap-6 rounded-2xl bg-white p-6 shadow md:p-10">
          <h2 className="text-cream-900 dark:text-cream-100 text-lg font-semibold">
            Credits
          </h2>
          <CreditEntries
            groups={data.credits}
            onChange={(credits) => setData("credits", credits)}
            reloadPropName="credit_search_results"
            queryParam="credit_q"
            creditTypeOptions={credit_type_options}
            errors={errors.credits}
            baseErrors={(errors as Record<string, string>).credit_base}
          />
        </div>
        <FormActions>
          <InertiaLink
            href={`/tracks/${track.id}/credits`}
            type="secondary"
            text="Cancel"
          />
          <Button
            text={processing ? "Saving..." : "Save"}
            type="primary"
            htmlButtonType="submit"
            disabled={processing}
          />
        </FormActions>
      </form>
    </div>
  );
};

export default Edit;
